import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { useCurrency } from "@/lib/currency";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CellInput } from "@/components/ui/cell-input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Palette, PoundSterling, BookOpen, Rocket, Upload, ImageIcon, Eye, PackagePlus, FileSpreadsheet, AlertTriangle, X } from "lucide-react";

const STEPS = [
  { id: 0, label: "Branding", icon: Palette, desc: "Set up your platform identity" },
  { id: 1, label: "Charge Rates", icon: PoundSterling, desc: "Configure hourly rates" },
  { id: 2, label: "Deliverables", icon: BookOpen, desc: "Import your deliverables library" },
  { id: 3, label: "Launch", icon: Rocket, desc: "Review and go live" },
];

const DEFAULT_RATES = [
  { role: "Director", charge_rate: 175, cost_rate: 95 },
  { role: "Associate", charge_rate: 145, cost_rate: 78 },
  { role: "Senior Engineer", charge_rate: 125, cost_rate: 65 },
  { role: "Engineer", charge_rate: 95, cost_rate: 50 },
  { role: "CAD/BIM Technician", charge_rate: 75, cost_rate: 38 },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { updateBranding } = useBranding();
  const { clearOnboarding } = useAuth();
  const cur = useCurrency();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Branding state
  const [brandingForm, setBrandingForm] = useState({
    platform_name: "MEP", platform_suffix: ".Engine", owner_name: "", company_name: "",
    tagline: "", login_title: "Structured MEP pricing for engineering consultancies",
    logo_url: "", nav_show_owner: true,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  // Rates state
  const [rates, setRates] = useState(DEFAULT_RATES);

  // Deliverables state
  const [delivImported, setDelivImported] = useState(false);
  const [delivCount, setDelivCount] = useState(0);
  const [importing, setImporting] = useState(false);

  // CSV import state
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvErrors, setCsvErrors] = useState([]);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const csvInputRef = useRef(null);

  // Load existing data
  useEffect(() => {
    Promise.all([
      API.get("/admin/branding").catch(() => ({ data: {} })),
      API.get("/admin/rates").catch(() => ({ data: [] })),
      API.get("/admin/deliverables").catch(() => ({ data: [] })),
    ]).then(([br, rt, dl]) => {
      if (br.data && Object.keys(br.data).length > 0) {
        setBrandingForm(prev => ({ ...prev, ...br.data }));
      }
      if (rt.data && rt.data.length > 0) setRates(rt.data);
      if (dl.data && dl.data.length > 0) { setDelivImported(true); setDelivCount(dl.data.length); }
    });
  }, []);

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5MB"); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile) return null;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", logoFile);
      const r = await API.post("/admin/branding/logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const logoUrl = `${BACKEND_URL}/api/admin/branding/logo?t=${Date.now()}`;
      setBrandingForm(prev => ({ ...prev, logo_url: logoUrl }));
      toast.success("Logo uploaded");
      return logoUrl;
    } catch (e) {
      toast.error("Logo upload failed");
      return null;
    } finally { setUploadingLogo(false); }
  };

  const saveBranding = async () => {
    let logoUrl = brandingForm.logo_url;
    if (logoFile) {
      const uploaded = await uploadLogo();
      if (uploaded) logoUrl = uploaded;
    }
    try {
      const payload = { ...brandingForm, logo_url: logoUrl };
      const r = await API.put("/admin/branding", payload);
      updateBranding(r.data);
      toast.success("Branding saved");
      return true;
    } catch { toast.error("Failed to save branding"); return false; }
  };

  const saveRates = async () => {
    try {
      await API.put("/admin/rates", { rates });
      toast.success("Rates saved");
      return true;
    } catch { toast.error("Failed to save rates"); return false; }
  };

  const importDeliverables = async () => {
    setImporting(true);
    try {
      const r = await API.post("/onboarding/import-deliverables");
      setDelivImported(true);
      setDelivCount(r.data.count);
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const handleCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".csv")) { toast.error("Please select a .csv file"); return; }
    setCsvFile(file);
    setCsvErrors([]);

    // Parse preview
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split("\n").filter(l => l.trim());
      if (lines.length < 2) { toast.error("CSV appears empty"); setCsvFile(null); return; }

      const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
      const rows = [];
      for (let i = 1; i < Math.min(lines.length, 11); i++) {
        const vals = lines[i].split(",").map(v => v.trim().replace(/^"|"$/g, ""));
        const row = {};
        headers.forEach((h, idx) => { row[h] = vals[idx] || ""; });
        rows.push(row);
      }
      setCsvPreview({ headers, rows, totalRows: lines.length - 1 });
    };
    reader.readAsText(file);
  };

  const uploadCsv = async () => {
    if (!csvFile) return;
    setUploadingCsv(true);
    setCsvErrors([]);
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      const r = await API.post("/onboarding/import-deliverables-csv?mode=append", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDelivImported(true);
      setDelivCount(r.data.total || r.data.count);
      if (r.data.warnings) setCsvErrors(r.data.warnings);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "CSV import failed");
    } finally { setUploadingCsv(false); }
  };

  const clearCsv = () => {
    setCsvFile(null);
    setCsvPreview(null);
    setCsvErrors([]);
    if (csvInputRef.current) csvInputRef.current.value = "";
  };

  const handleNext = async () => {
    if (step === 0) {
      const ok = await saveBranding();
      if (!ok) return;
    } else if (step === 1) {
      const ok = await saveRates();
      if (!ok) return;
    }
    setStep(step + 1);
  };

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      await API.post("/onboarding/complete", {
        steps: { branding: true, rates: true, deliverables: delivImported },
      });
      clearOnboarding();
      toast.success("Onboarding complete — your platform is ready!");
      navigate("/");
    } catch { toast.error("Failed to complete onboarding"); }
    finally { setSubmitting(false); }
  };

  const inputClass = "bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  const labelClass = "text-xs font-medium text-slate-400 mb-1.5 block";

  return (
    <div data-testid="onboarding-wizard" className="max-w-4xl mx-auto space-y-8">
      <div className="text-center">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-foreground">Welcome to FURD MEP.Engine</h1>
        <p className="text-base text-slate-400 mt-2">Let's set up your consultancy in a few quick steps</p>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-2" data-testid="onboarding-stepper">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2 flex-1">
            <button
              onClick={() => i <= step && setStep(i)}
              data-testid={`onboarding-step-${i}`}
              className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-sm font-medium transition-all w-full ${
                i === step ? "bg-blue-600 text-white shadow-lg shadow-blue-900/20" :
                i < step ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" :
                "bg-slate-800 text-slate-500"
              }`}
            >
              {i < step ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" strokeWidth={1.5} />}
              <div className="text-left hidden md:block">
                <p className="text-sm font-semibold">{s.label}</p>
                <p className="text-[10px] opacity-70">{s.desc}</p>
              </div>
            </button>
            {i < STEPS.length - 1 && <div className="w-4 h-px bg-slate-800 flex-shrink-0" />}
          </div>
        ))}
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-8">
          {/* Step 0: Branding */}
          {step === 0 && (
            <div className="space-y-6" data-testid="onboarding-branding">
              <div>
                <h2 className="font-heading text-xl font-bold text-white">Platform Identity</h2>
                <p className="text-sm text-slate-500 mt-1">Customise how the platform looks for your consultancy</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className={labelClass}>Platform Name</Label>
                  <Input value={brandingForm.platform_name} onChange={e => setBrandingForm({ ...brandingForm, platform_name: e.target.value })} className={inputClass} placeholder="MEP" data-testid="onboarding-platform-name" />
                </div>
                <div>
                  <Label className={labelClass}>Platform Suffix</Label>
                  <Input value={brandingForm.platform_suffix} onChange={e => setBrandingForm({ ...brandingForm, platform_suffix: e.target.value })} className={inputClass} placeholder=".Engine" data-testid="onboarding-platform-suffix" />
                </div>
                <div>
                  <Label className={labelClass}>Your Company Name</Label>
                  <Input value={brandingForm.company_name} onChange={e => setBrandingForm({ ...brandingForm, company_name: e.target.value })} className={inputClass} placeholder="Your Engineering Ltd" data-testid="onboarding-company-name" />
                </div>
                <div>
                  <Label className={labelClass}>Owner Label (shown in nav)</Label>
                  <Input value={brandingForm.owner_name} onChange={e => setBrandingForm({ ...brandingForm, owner_name: e.target.value })} className={inputClass} placeholder="FURD Tech" data-testid="onboarding-owner-name" />
                </div>
                <div className="sm:col-span-2">
                  <Label className={labelClass}>Login Page Title</Label>
                  <Input value={brandingForm.login_title} onChange={e => setBrandingForm({ ...brandingForm, login_title: e.target.value })} className={inputClass} data-testid="onboarding-login-title" />
                </div>
              </div>

              {/* Logo upload */}
              <div>
                <Label className={labelClass}>Company Logo</Label>
                <div className="flex items-center gap-4 mt-1">
                  <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-700 flex items-center justify-center bg-slate-950 overflow-hidden">
                    {logoPreview || brandingForm.logo_url ? (
                      <img src={logoPreview || brandingForm.logo_url} alt="Logo" className="w-full h-full object-contain" data-testid="onboarding-logo-preview" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-700" strokeWidth={1} />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoFile} className="hidden" data-testid="onboarding-logo-input" />
                    <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} className="border-slate-700 text-slate-300 hover:bg-slate-800 h-8" data-testid="onboarding-upload-logo-btn" disabled={uploadingLogo}>
                      <Upload className="w-3.5 h-3.5 mr-1.5" />{logoFile ? "Change file" : "Upload logo"}
                    </Button>
                    {logoFile && <p className="text-xs text-slate-500">{logoFile.name} ({(logoFile.size / 1024).toFixed(0)}KB)</p>}
                    <p className="text-[10px] text-slate-600">PNG, SVG, or JPEG. Max 5MB.</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox checked={brandingForm.nav_show_owner !== false} onCheckedChange={(v) => setBrandingForm({ ...brandingForm, nav_show_owner: !!v })} />
                <label className="text-sm text-slate-400">Show owner name in navigation bar</label>
              </div>

              {/* Live preview */}
              <div className="p-4 bg-slate-950 rounded-lg border border-slate-800">
                <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Eye className="w-3 h-3" />Live Preview</p>
                <div className="flex items-center gap-1">
                  {(logoPreview || brandingForm.logo_url) ? (
                    <img src={logoPreview || brandingForm.logo_url} alt="Logo" className="h-7" />
                  ) : (
                    <>
                      <span className="font-heading text-lg font-extrabold tracking-tight text-blue-500">{brandingForm.platform_name || "MEP"}</span>
                      <span className="font-heading text-lg font-bold tracking-tight text-slate-200">{brandingForm.platform_suffix || ".Engine"}</span>
                    </>
                  )}
                  {brandingForm.nav_show_owner !== false && brandingForm.owner_name && (
                    <span className="text-[9px] text-slate-600 ml-2 font-medium tracking-wider uppercase">by {brandingForm.owner_name}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Charge Rates */}
          {step === 1 && (
            <div className="space-y-6" data-testid="onboarding-rates">
              <div>
                <h2 className="font-heading text-xl font-bold text-white">Charge Rates</h2>
                <p className="text-sm text-slate-500 mt-1">Set the hourly charge rates and internal cost rates for each engineering role</p>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4 pb-2 border-b border-slate-800">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Charge Rate ({cur.symbol}/hr)</span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Cost Rate ({cur.symbol}/hr)</span>
                </div>
                {rates.map((r, i) => (
                  <div key={r.role} className="grid grid-cols-3 gap-4 items-center" data-testid={`onboarding-rate-${r.role.toLowerCase().replace(/[\s/]/g, '-')}`}>
                    <span className="text-sm font-medium text-slate-200">{r.role}</span>
                    <CellInput
                      type="number" value={r.charge_rate}
                      onChange={e => setRates(rates.map((x, j) => j === i ? { ...x, charge_rate: parseFloat(e.target.value) || 0 } : x))}
                      className={`${inputClass} font-mono`}
                      data-testid={`onboarding-charge-rate-${i}`}
                    />
                    <CellInput
                      type="number" value={r.cost_rate}
                      onChange={e => setRates(rates.map((x, j) => j === i ? { ...x, cost_rate: parseFloat(e.target.value) || 0 } : x))}
                      className={`${inputClass} font-mono`}
                      data-testid={`onboarding-cost-rate-${i}`}
                    />
                  </div>
                ))}
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-800">
                <p className="text-xs text-slate-500">These rates are used across all project pricing. You can adjust them later in Admin Settings.</p>
              </div>
            </div>
          )}

          {/* Step 2: Deliverables */}
          {step === 2 && (
            <div className="space-y-6" data-testid="onboarding-deliverables">
              <div>
                <h2 className="font-heading text-xl font-bold text-white">Deliverables Library</h2>
                <p className="text-sm text-slate-500 mt-1">Import deliverables from the standard RIBA library, upload your own CSV, or start from scratch</p>
              </div>

              {delivImported ? (
                <div className="p-6 bg-emerald-500/5 border border-emerald-500/20 rounded-lg text-center">
                  <Check className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                  <p className="text-lg font-heading font-bold text-emerald-400">{delivCount} Deliverables Loaded</p>
                  <p className="text-sm text-slate-400 mt-1">Your deliverables library is ready. You can edit and add more in Admin Settings.</p>
                  {csvErrors.length > 0 && (
                    <div className="mt-4 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg text-left">
                      <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5" />Import warnings ({csvErrors.length})</p>
                      {csvErrors.slice(0, 5).map((err, i) => <p key={`err-${i}`} className="text-xs text-slate-500">{err}</p>)}
                      {csvErrors.length > 5 && <p className="text-xs text-slate-600 mt-1">...and {csvErrors.length - 5} more</p>}
                    </div>
                  )}
                </div>
              ) : csvPreview ? (
                /* CSV Preview */
                <div className="space-y-4" data-testid="csv-preview-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-blue-400" />
                        {csvFile?.name}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{csvPreview.totalRows} rows found — showing first {csvPreview.rows.length}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={clearCsv} className="border-slate-700 text-slate-400 hover:bg-slate-800 h-7" data-testid="csv-clear-btn">
                      <X className="w-3 h-3 mr-1" />Clear
                    </Button>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-slate-800">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-800/50">
                          {csvPreview.headers.slice(0, 9).map(h => (
                            <TableHead key={h} className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 px-2 py-2 whitespace-nowrap">{h}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {csvPreview.rows.map((row, i) => (
                          <TableRow key={`csv-preview-${i}`} className="border-slate-800">
                            {csvPreview.headers.slice(0, 9).map(h => (
                              <TableCell key={h} className="text-xs text-slate-300 px-2 py-1.5 max-w-[150px] truncate">{row[h]}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <Button onClick={uploadCsv} disabled={uploadingCsv} data-testid="csv-import-confirm-btn" className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold h-11 shadow-lg shadow-blue-900/20">
                    <Upload className="w-4 h-4 mr-2" />{uploadingCsv ? "Importing..." : `Import ${csvPreview.totalRows} Deliverables`}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={importDeliverables}
                    disabled={importing}
                    data-testid="onboarding-import-deliverables"
                    className="p-6 rounded-lg border-2 border-blue-500/20 bg-blue-500/5 hover:border-blue-500/40 hover:bg-blue-500/10 transition-all text-left"
                  >
                    <PackagePlus className="w-8 h-8 text-blue-400 mb-3" strokeWidth={1.5} />
                    <p className="text-base font-heading font-bold text-white">{importing ? "Importing..." : "RIBA Standard Library"}</p>
                    <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                      42 deliverables across Mechanical, Electrical and Public Health disciplines covering RIBA Stages 2-6.
                    </p>
                  </button>
                  <button
                    onClick={() => csvInputRef.current?.click()}
                    data-testid="onboarding-csv-upload-btn"
                    className="p-6 rounded-lg border-2 border-amber-500/20 bg-amber-500/5 hover:border-amber-500/40 hover:bg-amber-500/10 transition-all text-left"
                  >
                    <input ref={csvInputRef} type="file" accept=".csv" onChange={handleCsvFile} className="hidden" data-testid="csv-file-input" />
                    <FileSpreadsheet className="w-8 h-8 text-amber-400 mb-3" strokeWidth={1.5} />
                    <p className="text-base font-heading font-bold text-white">Upload CSV</p>
                    <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                      Import your own deliverables from a spreadsheet. Columns: Name, Discipline, Stage, Description, and hours by role.
                    </p>
                  </button>
                  <button
                    onClick={() => { setDelivImported(true); setDelivCount(0); }}
                    data-testid="onboarding-skip-deliverables"
                    className="p-6 rounded-lg border-2 border-slate-700 hover:border-slate-600 bg-slate-800/50 transition-all text-left"
                  >
                    <BookOpen className="w-8 h-8 text-slate-500 mb-3" strokeWidth={1.5} />
                    <p className="text-base font-heading font-bold text-slate-300">Start from Scratch</p>
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                      Skip the import and manually add deliverables later in Admin Settings.
                    </p>
                  </button>
                </div>
              )}

              {/* CSV format help */}
              {!delivImported && !csvPreview && (
                <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-800">
                  <p className="text-xs font-semibold text-slate-400 mb-2">CSV Format Guide</p>
                  <p className="text-xs text-slate-500 leading-relaxed">Your CSV should have these columns (headers must be in the first row):</p>
                  <code className="block mt-2 text-[11px] text-slate-400 bg-slate-950 rounded p-2.5 font-mono overflow-x-auto">
                    Name, Discipline, Stage, Description, Director, Associate, Senior Engineer, Engineer, CAD/BIM Technician
                  </code>
                  <p className="text-[10px] text-slate-600 mt-2">Discipline values: Mechanical, Electrical, Public Health. Stage values: Stage 2, Stage 3, etc. Hour columns are optional — leave blank for zero.</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Review & Launch */}
          {step === 3 && (
            <div className="space-y-6" data-testid="onboarding-review">
              <div className="text-center">
                <Rocket className="w-12 h-12 text-blue-500 mx-auto mb-3" strokeWidth={1.5} />
                <h2 className="font-heading text-xl font-bold text-white">Ready to Launch</h2>
                <p className="text-sm text-slate-500 mt-1">Here's a summary of your setup</p>
              </div>

              <div className="divide-y divide-slate-800 bg-slate-800/30 rounded-lg border border-slate-800 overflow-hidden">
                <div className="flex justify-between items-center px-5 py-3">
                  <span className="text-sm text-slate-400">Platform</span>
                  <span className="text-sm font-medium text-white">{brandingForm.platform_name}{brandingForm.platform_suffix}</span>
                </div>
                {brandingForm.company_name && (
                  <div className="flex justify-between items-center px-5 py-3">
                    <span className="text-sm text-slate-400">Company</span>
                    <span className="text-sm font-medium text-white">{brandingForm.company_name}</span>
                  </div>
                )}
                {brandingForm.owner_name && (
                  <div className="flex justify-between items-center px-5 py-3">
                    <span className="text-sm text-slate-400">Owner</span>
                    <span className="text-sm font-medium text-white">{brandingForm.owner_name}</span>
                  </div>
                )}
                <div className="flex justify-between items-center px-5 py-3">
                  <span className="text-sm text-slate-400">Logo</span>
                  <span className="text-sm font-medium text-white">{logoPreview || brandingForm.logo_url ? "Uploaded" : "Text-based"}</span>
                </div>
                <div className="flex justify-between items-center px-5 py-3">
                  <span className="text-sm text-slate-400">Charge Rates</span>
                  <span className="text-sm font-medium text-white">{rates.length} roles configured</span>
                </div>
                <div className="flex justify-between items-center px-5 py-3">
                  <span className="text-sm text-slate-400">Deliverables</span>
                  <span className="text-sm font-medium text-white">{delivCount > 0 ? `${delivCount} deliverables imported` : "Starting from scratch"}</span>
                </div>
              </div>

              <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                <p className="text-xs text-slate-400 leading-relaxed">
                  You can always modify these settings later from <strong className="text-slate-200">Admin Settings</strong>. Completing onboarding will take you to the dashboard where you can start creating projects.
                </p>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-800">
            <Button
              variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
              data-testid="onboarding-prev" className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            {step < 3 ? (
              <Button onClick={handleNext} data-testid="onboarding-next" className="bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-lg shadow-blue-900/20">
                Save & Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleComplete} disabled={submitting} data-testid="onboarding-complete" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-lg shadow-emerald-900/20">
                {submitting ? "Completing..." : "Complete Setup & Go to Dashboard"} <Rocket className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
